import {v4 as uuid} from 'uuid';
import {Asset, queryLastDatapointsValue} from '@deviceinsight/ng-ui-api-client';
import {
	ACTION_TYPE_DEVICE,
	ACTION_TYPE_EMAIL,
	DEVICE_ACTION_TYPE,
	DeviceType,
	ACTION_TYPE_WEBSOCKET
} from '../../../modules/ruleengine/config/consts';
import type {
	Action,
	ActionType,
	AvailableActionType,
	DeviceAction,
	DeviceActionState,
	DeviceActionType,
	DeviceElementTarget,
	EmailAction,
	UiAction,
	UiDeviceAction,
	UiEmailAction,
	UiWebsocketAction,
	WebsocketAction
} from '../../../modules/ruleengine/types';
import {getAssetDeviceType, getAssetFromCacheOrFetch} from '../ruleEditHelpers';
import {
	targetButton1Color,
	targetButton2Color,
	targetGpio1,
	targetGpio2,
	targetGpio3,
	targetGpio4,
	targetLightColor
} from '../../../modules/ruleengine/config/rulestructure';
import {
	DATAPOINT_PIN1_MODE,
	DATAPOINT_PIN2_MODE,
	DATAPOINT_PIN3_MODE,
	DATAPOINT_PIN4_MODE,
	GPIO_MODE_MAPPING,
	GPIO_MODE_OUTPUT
} from '../../../modules/common/consts';
export const createAction = (actionType: ActionType): UiAction => {
	const metadata = {
		uuid: uuid(),
		isValid: false
	};

	switch (actionType) {
		case ACTION_TYPE_EMAIL:
			return {
				actionType: ACTION_TYPE_EMAIL,
				receiverEmail: undefined,
				emailTemplateId: undefined,
				metadata
			};

		case ACTION_TYPE_WEBSOCKET:
			return {
				actionType: ACTION_TYPE_WEBSOCKET,
				metadata
			};

		default:
			// ACTION_TYPE_DEVICE:
			return {
				actionType: ACTION_TYPE_DEVICE,
				deviceActionType: undefined,
				targetValue: undefined,
				dataAddress: {
					assetUrn: undefined,
					assetId: undefined,
					dataPoint: undefined
				},
				targetFrequency: undefined,
				metadata
			};
	}
};

const buildDeviceAction = (action: UiDeviceAction): DeviceAction => {
	const {actionType, targetValue, dataAddress, targetFrequency, deviceActionType} = action;
	return {
		actionType,
		deviceActionType,
		targetValue,
		dataAddress,
		targetFrequency
	};
};

const buildEmailAction = (action: UiEmailAction): EmailAction => {
	const {actionType, receiverEmail, emailTemplateId} = action;
	return {
		actionType,
		emailAddresses: receiverEmail ? [receiverEmail] : [],
		emailTemplateId
	};
};

const buildWebsocketAction = (action: UiWebsocketAction): WebsocketAction => {
	const {actionType} = action;
	return {
		actionType
	};
};

const buildDeviceUiAction = (action: DeviceAction): UiDeviceAction => {
	const {actionType, dataAddress, targetValue, deviceActionType, targetFrequency} = action;
	return {
		actionType,
		deviceActionType,
		targetValue,
		dataAddress,
		targetFrequency,
		metadata: {
			uuid: uuid(),
			isValid: true
		}
	};
};

const buildEmailUiAction = (action: EmailAction): UiEmailAction => {
	const {actionType, emailAddresses, emailTemplateId} = action;
	return {
		actionType,
		receiverEmail: emailAddresses && emailAddresses.length ? emailAddresses[0] : undefined,
		emailTemplateId,
		metadata: {
			uuid: uuid(),
			isValid: true
		}
	};
};

const buildWebsocketUiAction = (action: WebsocketAction): UiWebsocketAction => {
	const {actionType} = action;
	return {
		actionType,
		metadata: {
			uuid: uuid(),
			isValid: true
		}
	};
};

export const mapApiActionToUiAction = (action: Action): UiAction => {
	switch (action.actionType) {
		case ACTION_TYPE_DEVICE:
			return buildDeviceUiAction(action);

		case ACTION_TYPE_EMAIL:
			return buildEmailUiAction(action);

		case ACTION_TYPE_WEBSOCKET:
			return buildWebsocketUiAction(action);

		default:
			throw new Error('unknown action type');
	}
};
export const mapUiActionToApiAction = (uiAction: UiAction): Action => {
	switch (uiAction.actionType) {
		case ACTION_TYPE_DEVICE:
			return buildDeviceAction(uiAction);

		case ACTION_TYPE_EMAIL:
			return buildEmailAction(uiAction);

		case ACTION_TYPE_WEBSOCKET:
			return buildWebsocketAction(uiAction);

		default:
			throw new Error('unknown action type');
	}
};
export const findAvailableActionsForAsset = (asset: Asset | null | undefined): Array<AvailableActionType> => {
	if (asset) {
		const assetDeviceType = getAssetDeviceType(asset);

		switch (assetDeviceType) {
			case DeviceType.sBox:
				return [
					{
						value: 'setLed',
						defaultLabel: 'Set LED color',
						labelId: 'rafi.ruleengine.action.setLed'
					},
					{
						value: 'setPin',
						defaultLabel: 'Set GPIO',
						labelId: 'rafi.ruleengine.action.setPin'
					}
				];

			case DeviceType.sLight:
				return [
					{
						value: 'setLed',
						defaultLabel: 'Set LED color',
						labelId: 'rafi.ruleengine.action.setLed'
					}
				];

			default:
				return [];
		}
	} else {
		return [];
	}
};
export const initDeviceActionState = async (action: UiDeviceAction): Promise<DeviceActionState> => {
	const {targetValue, dataAddress, deviceActionType, targetFrequency} = action;

	if (dataAddress && dataAddress.assetUrn) {
		const {dataPoint} = dataAddress;
		const asset = await getAssetFromCacheOrFetch(dataAddress.assetUrn);
		const availablePins = await getGpiosWithOutputMode(asset);
		const availableDeviceActionTypes = findAvailableActionsForAsset(asset);
		const deviceType = getAssetDeviceType(asset);
		const availableElements = getActionElementsForDeviceActionType(deviceType, deviceActionType, availablePins);
		const selectedElement = availableElements.find((element) => element.dataPoint === dataPoint);
		return {
			asset,
			deviceActionType,
			targetValue,
			dataPoint,
			targetFrequency,
			availableDeviceActionTypes,
			availableElements,
			selectedElement,
			availablePins
		};
	} else {
		return Promise.resolve({
			asset: undefined,
			deviceActionType: undefined,
			targetValue: undefined,
			dataPoint: undefined,
			targetFrequency: undefined,
			availableDeviceActionTypes: [],
			availableElements: [],
			availablePins: [],
			selectedElement: undefined
		});
	}
};
export const validateDeviceActionState = (state: DeviceActionState): boolean => {
	const {asset, deviceActionType, targetValue, dataPoint} = state;

	if (!asset || !deviceActionType || !targetValue || !dataPoint) {
		return false;
	}

	return true;
};

const filterPins = (availablePins: Array<string>): Array<DeviceElementTarget> => {
	return [targetGpio1, targetGpio2, targetGpio3, targetGpio4].filter((target) =>
		availablePins.includes(target.dataPoint)
	);
};

export const getActionElementsForDeviceActionType = (
	deviceType: string,
	deviceActionType: DeviceActionType | null | undefined,
	availablePins: Array<string>
): Array<DeviceElementTarget> => {
	if (deviceType === DeviceType.sBox) {
		switch (deviceActionType) {
			case DEVICE_ACTION_TYPE.SET_LED:
				return [targetButton1Color, targetButton2Color];

			case DEVICE_ACTION_TYPE.SET_GPIO:
				return filterPins(availablePins);

			default:
				return [];
		}
	} else if (deviceType === DeviceType.sLight) {
		switch (deviceActionType) {
			case DEVICE_ACTION_TYPE.SET_LED:
				return [targetLightColor];

			case DEVICE_ACTION_TYPE.SET_GPIO:
				return filterPins(availablePins);

			default:
				return [];
		}
	} else if (deviceType === DeviceType.kisIo) {
		switch (deviceActionType) {
			case DEVICE_ACTION_TYPE.SET_GPIO:
				return filterPins(availablePins);

			default:
				return [];
		}
	} else {
		return [];
	}
};
export const getGpiosWithOutputMode = async (asset: Asset): Promise<Array<string>> => {
	const data = await queryLastDatapointsValue(
		asset.urn,
		[DATAPOINT_PIN1_MODE, DATAPOINT_PIN2_MODE, DATAPOINT_PIN3_MODE, DATAPOINT_PIN4_MODE],
		'TEXT'
	);
	return data.asset.datapoints
		.filter((dp) => dp.values.at(0)?.at(1) === GPIO_MODE_OUTPUT)
		.map((dp) => GPIO_MODE_MAPPING[dp.name]);
};
